/**
 * LangGraph API Service
 * Connects frontend to LangGraph workflow endpoints
 */

import apiClient from './apiClient';

// Types
export interface NegotiationRequest {
  supplier_id: string;
  supplier_name: string;
  target_price: number;
  current_price: number;
  volume: number;
  category?: string;
  batna?: string;
  max_rounds?: number;
}

export interface NegotiationResponse {
  session_id: string;
  status: 'active' | 'paused' | 'completed' | 'failed';
  initial_price: number;
  target_price: number;
  final_price: number | null;
  price_reduction_pct: number | null;
  rounds_count: number;
  recommended_action: string;
  strategy: string | null;
  confidence_score: number;
  alternative_approaches: string[];
  email_draft: string;
  next_steps: string[];
}

export interface NegotiationSession {
  session_id: string;
  supplier_id: string;
  status: string;
  current_round: number;
  offers: Array<{
    round: number;
    our_offer: number;
    their_counter?: number;
    status: string;
    timestamp: string;
  }>;
  learning_data: {
    supplier_flexibility: number;
    price_movement: number;
    term_flexibility: number;
  };
}

export interface LangGraphAgent {
  id: string;
  name: string;
  type: string;
  status: string;
  capabilities: string[];
}

export interface ResearchRequest {
  query: string;
  sources?: string[];
  max_results?: number;
}

export interface ResearchResponse {
  query: string;
  results: Array<{
    source: string;
    title: string;
    content: string;
    relevance_score: number;
  }>;
  summary: string;
  generated_at: string;
}

// API Functions

/**
 * Get list of available LangGraph agents
 */
export const getLangGraphAgents = async (): Promise<LangGraphAgent[]> => {
  const response = await apiClient.get<LangGraphAgent[]>('/langgraph/agents');
  return response.data;
};

/**
 * Start a new AI-powered negotiation session
 */
export const startNegotiation = async (request: NegotiationRequest): Promise<NegotiationResponse> => {
  const response = await apiClient.post<NegotiationResponse>('/langgraph/negotiate', request);
  return response.data;
};

/**
 * Get the status and details of an ongoing negotiation session
 */
export const getNegotiationSession = async (sessionId: string): Promise<NegotiationSession> => {
  const response = await apiClient.get<NegotiationSession>(`/langgraph/negotiate/${sessionId}`);
  return response.data;
};

/**
 * Continue a paused negotiation session
 */
export const continueNegotiation = async (
  sessionId: string, 
  action: 'continue' | 'accept' | 'reject'
): Promise<NegotiationResponse> => {
  const response = await apiClient.post<NegotiationResponse>(`/langgraph/negotiate/${sessionId}/continue`, {
    action
  });
  return response.data;
};

/**
 * Perform OSINT research using LangGraph agents
 */
export const performResearch = async (request: ResearchRequest): Promise<ResearchResponse> => {
  const response = await apiClient.post<ResearchResponse>('/langgraph/research', request);
  return response.data;
};

// Export all functions
export default {
  getLangGraphAgents,
  startNegotiation,
  getNegotiationSession,
  continueNegotiation,
  performResearch,
};
